import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Container, Typography, Box, Button, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Paper,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Grid, Select, MenuItem, InputLabel, FormControl,
    Switch, FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Layers as LayersIcon } from '@mui/icons-material';
import AppHeader from '../../components/AppHeader';

const OTPolicyManager = ({ setIsLoggedIn }) => {
    const [profiles, setProfiles] = useState([]);
    const [open, setOpen] = useState(false);
    const [editingProfile, setEditingProfile] = useState(null);
    const [formData, setFormData] = useState({
        profile_code: '',
        profile_name: '',
        calc_method: 'HOURLY',
        normal_day_rate: 2.0,
        normal_day_limit: 3.0,
        off_day_rate: 2.0,
        off_day_limit: 5.0,
        rest_day_rate: 2.0,
        rest_day_limit: 8.0,
        holiday_rate: 2.0,
        holiday_limit: 8.0,
        formula_basis: '48HRS',
        custom_divisor_days: '',
        normal_formula_basis: '48HRS',
        normal_divisor_days: '',
        rest_formula_basis: 'PAYROLL_DAYS',
        rest_divisor_days: '',
        off_day_formula_basis: 'PAYROLL_DAYS',
        off_day_divisor_days: '',
        holiday_formula_basis: 'PAYROLL_DAYS',
        holiday_divisor_days: '',
        is_active: 1
    });

    const [tiersOpen, setTiersOpen] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [tiers, setTiers] = useState([]);

    const handleOpenTiers = async (profile) => {
        setSelectedProfile(profile);
        try {
            const tenantHeader = { 'X-Tenant': localStorage.getItem('tenant') };
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/ot-profiles/${profile.profile_code}/tiers`, {
                withCredentials: true,
                headers: tenantHeader
            });
            setTiers(res.data.data || []);
        } catch (error) {
            console.error("Error fetching tiers:", error);
            setTiers([]);
        }
        setTiersOpen(true);
    };

    const handleCloseTiers = () => {
        setTiersOpen(false);
        setSelectedProfile(null);
        setTiers([]);
    };

    const handleSaveTiers = async () => {
        try {
            const tenantHeader = { 'X-Tenant': localStorage.getItem('tenant') };
            await axios.post(
                `${process.env.REACT_APP_API_URL1}/sm/ot-profiles/${selectedProfile.profile_code}/tiers`,
                { tiers },
                {
                    withCredentials: true,
                    headers: tenantHeader
                }
            );
            handleCloseTiers();
        } catch (error) {
            console.error("Error saving tiers:", error);
            alert("Error saving daily tiers configuration.");
        }
    };

    useEffect(() => {
        fetchProfiles();
    }, []);

    const fetchProfiles = async () => {
        try {
            const tenantHeader = { 'X-Tenant': localStorage.getItem('tenant') };
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/ot-profiles`, {
                withCredentials: true,
                headers: tenantHeader
            });
            setProfiles(res.data.data);
        } catch (error) {
            console.error("Error fetching profiles:", error);
        }
    };

    const handleOpen = (profile = null) => {
        if (profile) {
            setEditingProfile(profile);
            setFormData(profile);
        } else {
            setEditingProfile(null);
            setFormData({
                profile_code: '',
                profile_name: '',
                calc_method: 'HOURLY',
                normal_day_rate: 2.0,
                normal_day_limit: 3.0,
                off_day_rate: 2.0,
                off_day_limit: 5.0,
                rest_day_rate: 2.0,
                rest_day_limit: 8.0,
                holiday_rate: 2.0,
                holiday_limit: 8.0,
                formula_basis: '48HRS',
                custom_divisor_days: '',
                normal_formula_basis: '48HRS',
                normal_divisor_days: '',
                rest_formula_basis: 'PAYROLL_DAYS',
                rest_divisor_days: '',
                off_day_formula_basis: 'PAYROLL_DAYS',
                off_day_divisor_days: '',
                holiday_formula_basis: 'PAYROLL_DAYS',
                holiday_divisor_days: '',
                is_active: 1
            });
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));
    };

    const handleSubmit = async () => {
        try {
            const tenantHeader = { 'X-Tenant': localStorage.getItem('tenant') };
            await axios.post(`${process.env.REACT_APP_API_URL1}/sm/ot-profiles`, formData, {
                withCredentials: true,
                headers: tenantHeader
            });
            fetchProfiles();
            handleClose();
        } catch (error) {
            console.error("Error saving profile:", error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this profile?")) {
            try {
                const tenantHeader = { 'X-Tenant': localStorage.getItem('tenant') };
                await axios.delete(`${process.env.REACT_APP_API_URL1}/sm/ot-profiles/${id}`, {
                    withCredentials: true,
                    headers: tenantHeader
                });
                fetchProfiles();
            } catch (error) {
                console.error("Error deleting profile:", error);
            }
        }
    };

    const renderFormulaFields = (label, prefix) => {
        const basisField = `${prefix}_formula_basis`;
        const divisorField = `${prefix}_divisor_days`;
        return (
            <>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <InputLabel>{label} Formula Basis</InputLabel>
                        <Select
                            name={basisField}
                            value={formData[basisField] || 'PAYROLL_DAYS'}
                            onChange={handleChange}
                            label={`${label} Formula Basis`}
                        >
                            <MenuItem value="44HRS">44 Hours / Week</MenuItem>
                            <MenuItem value="48HRS">48 Hours / Week</MenuItem>
                            <MenuItem value="PAYROLL_DAYS">Payroll Period Days</MenuItem>
                            <MenuItem value="CUSTOM_DAYS">Fixed Custom Days</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                {formData[basisField] === 'CUSTOM_DAYS' && (
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            required
                            type="number"
                            inputProps={{ min: 0.01, step: 0.01 }}
                            label={`${label} Divisor Days`}
                            name={divisorField}
                            value={formData[divisorField] || ''}
                            onChange={handleChange}
                            helperText="Hourly rate = monthly salary ÷ divisor days ÷ hours per day"
                        />
                    </Grid>
                )}
            </>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f1f5f9' }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        Overtime Rate Profiles
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpen()}
                        sx={{ backgroundColor: '#2563eb', '&:hover': { backgroundColor: '#1d4ed8' }, borderRadius: 2 }}
                    >
                        Create Profile
                    </Button>
                </Box>

                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Normal Day (Rate | Limit)</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Off Day (Rate | Limit)</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Rest Day (Rate | Limit)</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Holiday (Rate | Limit)</TableCell>
                                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {profiles.map((p) => (
                                <TableRow key={p.id} hover>
                                    <TableCell>{p.profile_code}</TableCell>
                                    <TableCell>{p.profile_name}</TableCell>
                                    <TableCell>{p.calc_method}</TableCell>
                                    <TableCell>{p.normal_day_rate}x | {p.normal_day_limit}h</TableCell>
                                    <TableCell>{p.off_day_rate}x | {p.off_day_limit}h</TableCell>
                                    <TableCell>{p.rest_day_rate}x | {p.rest_day_limit}h</TableCell>
                                    <TableCell>{p.holiday_rate}x | {p.holiday_limit}h</TableCell>
                                    <TableCell align="center">
                                        <IconButton color="secondary" onClick={() => handleOpenTiers(p)} title="Configure Daily Tiers">
                                            <LayersIcon />
                                        </IconButton>
                                        <IconButton color="primary" onClick={() => handleOpen(p)}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => handleDelete(p.id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Container>

            {/* Create/Edit Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid #e2e8f0', mb: 2 }}>
                    {editingProfile ? 'Edit OT Profile' : 'New OT Profile'}
                </DialogTitle>
                <DialogContent sx={{ pt: '12px !important' }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Profile Code"
                                name="profile_code"
                                value={formData.profile_code}
                                onChange={handleChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Profile Name"
                                name="profile_name"
                                value={formData.profile_name}
                                onChange={handleChange}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Calc Method</InputLabel>
                                <Select name="calc_method" value={formData.calc_method} onChange={handleChange} label="Calc Method">
                                    <MenuItem value="HOURLY">Hourly Calculation</MenuItem>
                                    <MenuItem value="DAILY">Daily Flat Rate</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Normal Day */}
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="number" step="0.5" label="Normal Day Rate Multiplier" name="normal_day_rate" value={formData.normal_day_rate} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="number" step="1" label="Normal Day Max Hours" name="normal_day_limit" value={formData.normal_day_limit} onChange={handleChange} />
                        </Grid>
                        {renderFormulaFields('Normal Day', 'normal')}

                        {/* Off Day */}
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="number" step="0.5" label="Off Day Rate Multiplier (e.g. Saturday)" name="off_day_rate" value={formData.off_day_rate} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="number" step="1" label="Off Day Max Hours" name="off_day_limit" value={formData.off_day_limit} onChange={handleChange} />
                        </Grid>
                        {renderFormulaFields('Off Day', 'off_day')}

                        {/* Rest Day */}
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="number" step="0.5" label="Rest Day Rate Multiplier (e.g. Sunday)" name="rest_day_rate" value={formData.rest_day_rate} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="number" step="1" label="Rest Day Max Hours" name="rest_day_limit" value={formData.rest_day_limit} onChange={handleChange} />
                        </Grid>
                        {renderFormulaFields('Rest Day', 'rest')}

                        {/* Holiday */}
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="number" step="0.5" label="Holiday Rate Multiplier" name="holiday_rate" value={formData.holiday_rate} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="number" step="1" label="Holiday Max Hours" name="holiday_limit" value={formData.holiday_limit} onChange={handleChange} />
                        </Grid>
                        {renderFormulaFields('Holiday', 'holiday')}

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={formData.is_active === 1} onChange={handleChange} name="is_active" />}
                                label="Active Policy"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={handleClose} sx={{ color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} sx={{ backgroundColor: '#2563eb' }}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Configure Tiers Dialog */}
            <Dialog open={tiersOpen} onClose={handleCloseTiers} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid #e2e8f0', mb: 2 }}>
                    Configure Daily Flat Rate Tiers - {selectedProfile?.profile_name} ({selectedProfile?.profile_code})
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button 
                            variant="outlined" 
                            startIcon={<AddIcon />} 
                            onClick={() => setTiers([...tiers, { day_type_category: 'REST', min_hours: 0.01, max_hours: 4.00, payable_hours: 4.00 }])}
                        >
                            Add Tier
                        </Button>
                    </Box>
                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0' }}>
                        <Table>
                            <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Min Hours</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Max Hours</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Payable Hours</TableCell>
                                    <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tiers.map((tier, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={tier.day_type_category}
                                                    onChange={(e) => {
                                                        const updated = [...tiers];
                                                        updated[index].day_type_category = e.target.value;
                                                        setTiers(updated);
                                                    }}
                                                >
                                                    <MenuItem value="REST">REST Day</MenuItem>
                                                    <MenuItem value="HOLIDAY">HOLIDAY</MenuItem>
                                                    <MenuItem value="NORMAL">NORMAL Day</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                type="number"
                                                size="small"
                                                inputProps={{ step: 0.01 }}
                                                value={tier.min_hours}
                                                onChange={(e) => {
                                                    const updated = [...tiers];
                                                    updated[index].min_hours = parseFloat(e.target.value) || 0;
                                                    setTiers(updated);
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                type="number"
                                                size="small"
                                                inputProps={{ step: 0.01 }}
                                                value={tier.max_hours}
                                                onChange={(e) => {
                                                    const updated = [...tiers];
                                                    updated[index].max_hours = parseFloat(e.target.value) || 0;
                                                    setTiers(updated);
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                type="number"
                                                size="small"
                                                inputProps={{ step: 0.01 }}
                                                value={tier.payable_hours}
                                                onChange={(e) => {
                                                    const updated = [...tiers];
                                                    updated[index].payable_hours = parseFloat(e.target.value) || 0;
                                                    setTiers(updated);
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton 
                                                color="error" 
                                                onClick={() => {
                                                    const updated = tiers.filter((_, idx) => idx !== index);
                                                    setTiers(updated);
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {tiers.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 3, color: '#64748b' }}>
                                            No tiers configured. Click 'Add Tier' to configure daily flat rates.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={handleCloseTiers} sx={{ color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveTiers} sx={{ backgroundColor: '#2563eb' }}>Save Configuration</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OTPolicyManager;
